import Data from '@/data'
import { stateGetItem, stateRemoveItem } from '@/logic/stateReducers'
import { ProfileImported } from '@/types/api'
import { CallbackWrap } from '@/types/common'
import {
  AscendancyEmbed,
  AtlasEmbed,
  BuildListEmbed,
  CraftingEmbed,
  Embed,
  EquipmentEmbed,
  LinkEmbed,
  PantheonEmbed,
  PassivesEmbed,
  SkillsEmbed,
  SkillsEmbedSkill
} from '@/types/embed'
import { Item } from '@/types/item'
import { LevelingEmbed } from '@/types/leveling'
import { defaultPlayerClass, ItemOrId, PlayerClass, PobBuild, Profile } from '@/types/profile'
import { getUniqueId } from '@maxroll-media-group/common-libs'
import React, { ReactNode } from 'react'
import createStore from './factory'

const identity = <State,>(state: State) => state

export type StoreState = {
  id: number
  embeds: Embed[]
  planner?: Profile
  activeEmbed: number
  items: { [key in number]?: Item }
  passive_tree?: PassivesEmbed | AtlasEmbed | undefined
  pobBuild?: PobBuild
  imported?: ProfileImported | undefined
}

export const baseSkillsVariant: { name: string; skills: SkillsEmbedSkill[] } = { name: 'Default', skills: [] }

export function defaultPlanner(playerClass: PlayerClass = defaultPlayerClass): Profile {
  return {
    class: playerClass,
    version: Data.defaultVersion,
    level: 100,
    equipment: {
      variants: [
        { id: getUniqueId(), name: 'Set 1', items: {} },
        { id: getUniqueId(), name: 'Set 2', items: {} },
        { id: getUniqueId(), name: 'Set 3', items: {} }
      ],
      active: 0
    },
    mercenary: {
      variants: [{ id: getUniqueId(), name: 'Set 1', items: {} }],
      active: 0
    },
    skills: {
      steps: [
        {
          name: 'Default',
          skills: []
        }
      ],
      step: 0
    },
    passives: {
      variants: EmbedStore.passives.variants,
      active: 0
    },
    atlas: {
      variants: EmbedStore.atlas.variants,
      active: 0
    },
    pantheon: {}
  }
}

// This is not an actual store
export class EmbedStore {
  static actions = {
    setName(state: StoreState, embed: Embed, name: string) {
      if (name === embed.name) return embed
      return { ...embed, name }
    }
  }

  static equipment: EquipmentEmbed = {
    id: 0,
    type: 'equipment',
    name: 'Equipment',
    items: {}
  }
  static pantheon: PantheonEmbed = {
    id: 0,
    type: 'pantheon',
    name: 'Pantheon',
    active: {}
  }
  static skills: SkillsEmbed = {
    id: 0,
    type: 'skills',
    name: 'Skills',
    steps: [
      {
        name: 'Step 1',
        skills: []
      }
    ],
    step: 0
  }
  static ['skills-video-display']: SkillsEmbed = {
    id: 0,
    type: 'skills-video-display',
    name: 'Skills',
    steps: [
      {
        name: 'Step 1',
        skills: []
      }
    ],
    step: 0
  }
  static link: LinkEmbed = {
    id: 0,
    type: 'link',
    name: 'Gem Link',
    gems: []
  }
  static ascendancy: AscendancyEmbed = {
    id: 0,
    type: 'ascendancy',
    version: 325,
    name: 'Ascendancy',
    ascendancy: 'Ascendant',
    steps: [[]]
  }
  static passives: PassivesEmbed = {
    id: 0,
    type: 'passives',
    name: 'Passive Tree',
    charClass: 'StrDexInt',
    ascendancy: 'Ascendant',
    version: 325,
    variants: [
      {
        name: 'Variant 1',
        history: [],
        masteries: {},
        jewels: {},
        tattoos: {},
        notes: []
      }
    ],
    active: 0
  }
  static atlas: AtlasEmbed = {
    id: 0,
    type: 'atlas',
    name: 'Atlas Passive Tree',
    version: 324,
    variants: [
      {
        name: 'Variant 1',
        history: [],
        masteries: {},
        jewels: {},
        tattoos: {},
        notes: []
      }
    ],
    active: 0
  }
  static leveling: LevelingEmbed = {
    id: 0,
    type: 'leveling',
    name: 'Leveling Guide',
    steps: [{ level: 0 }],
    showTipsAndTricks: true,
    levelingSteps: [],
    overview: {},
    tabs: [
      { id: 'overview', name: 'Overview' },
      { id: 'leveling', name: 'Leveling' },
      { id: 'progression', name: 'Progression' }
    ],
    extraTabs: [],
    pobs: []
  }
  static crafting: CraftingEmbed = {
    id: 0,
    type: 'crafting',
    name: 'Crafting Tool',
    items: []
  }

  static buildlist: BuildListEmbed = {
    id: 0,
    type: 'buildlist',
    name: 'Build Database',
    builds: []
  }
}

const RootStoreBase = createStore(
  {
    addEmbed(state, embed: Embed | ((state: StoreState) => Embed)) {
      let newEmbed = typeof embed === 'function' ? embed(state) : embed
      if (!newEmbed) return state
      newEmbed = { ...newEmbed, id: 0 }
      do {
        newEmbed.id += 1
      } while (state.embeds.find((e) => e.id === newEmbed.id))
      return {
        ...state,
        embeds: [...state.embeds, newEmbed],
        activeEmbed: state.embeds.length
      }
    },

    setEmbed(state, value: Embed | ((prev: Embed, state: StoreState) => Embed)) {
      const prev = state.embeds[state.activeEmbed]
      if (!prev) return state
      const newEmbed = typeof value === 'function' ? value(prev, state) : value
      if (newEmbed === prev) return state
      state = { ...state, embeds: state.embeds.slice() }
      state.embeds[state.activeEmbed] = newEmbed
      return state
    },

    setActiveEmbed(state, id: number) {
      const index = state.embeds.findIndex((embed) => embed.id === id)
      if (index === state.activeEmbed) return state
      return { ...state, activeEmbed: index }
    },

    renameEmbed(state, id: number, name: string) {
      const index = state.embeds.findIndex((embed) => embed.id === id)
      if (index < 0 || state.embeds[index].name === name) return state
      state = { ...state }
      state.embeds = state.embeds.slice()
      state.embeds[index] = { ...state.embeds[index], name }
      return state
    },

    moveEmbed(state, from: number, to: number) {
      if (from === to) return state
      state = { ...state, embeds: state.embeds.slice() }
      const activeId = state.embeds[state.activeEmbed]?.id
      const src = state.embeds.splice(from, 1)
      state.embeds.splice(to, 0, ...src)
      state.activeEmbed = state.embeds.findIndex((embed) => embed.id === activeId)
      return state
    },

    deleteEmbed(state, id: number) {
      const index = state.embeds.findIndex((embed) => embed.id === id)
      if (index < 0) return state
      state = { ...state }
      state.embeds = state.embeds.slice()
      state.embeds.splice(index, 1)
      if (state.activeEmbed >= state.embeds.length) {
        state.activeEmbed = state.embeds.length - 1
      }
      return state
    },

    setPlanner(state, value: Profile | ((profile: Profile) => Profile)) {
      if (typeof value === 'function') value = value(state.planner ?? defaultPlanner())
      return { ...state, planner: value }
    },

    setState(state, value: StoreState | ((state: StoreState) => StoreState)) {
      if (typeof value === 'function') value = value(state)
      value = { ...value }
      if (!value.embeds[value.activeEmbed]) {
        value.activeEmbed = -1
      }
      return value
    },

    setItem(
      state,
      id: number,
      value: Item | undefined | ((item: Item | undefined, state: StoreState) => Item | undefined)
    ) {
      const prev = state.items[id]
      if (typeof value === 'function') value = value(prev, state)
      if (value === prev) return state
      if (!value) return stateRemoveItem(state, id)
      state = { ...state }
      state.items = { ...state.items }
      state.items[id] = value
      return state
    },

    setImported(state, value: CallbackWrap<ProfileImported | undefined>) {
      const prev = state.imported
      if (typeof value === 'function') value = value(prev)
      if (value === prev) return state
      return { ...state, imported: value }
    }
  },
  {
    id: 0,
    embeds: [],
    activeEmbed: -1,
    items: {}
  } as StoreState
)

type RootStoreInstance = typeof RootStoreBase extends new () => infer Type ? Type : never

type DispatchSubMethods<State, Reducers> = {
  [key in keyof Reducers]: Reducers[key] extends (state: StoreState, subState: State, ...args: infer Args) => State
    ? (...args: Args) => void
    : never
}

type StoreDispatch = RootStoreInstance['dispatch'] & {
  embed: DispatchSubMethods<Embed, typeof EmbedStore.actions>
}

export default class Store extends RootStoreBase {
  static uniqueId = 1
  static newId() {
    return ++Store.uniqueId
  }

  declare dispatch: StoreDispatch

  constructor(initialState?: Partial<StoreState>) {
    super(initialState)
    const embedActions: Partial<typeof this.dispatch.embed> = {}
    for (const type in EmbedStore.actions) {
      const action = type as any as keyof typeof EmbedStore.actions
      embedActions[action] = (...args: any) =>
        this.dispatch.setEmbed((embed, state) =>
          (EmbedStore.actions[action] as (state: StoreState, embed: Embed, ...args: any) => Embed)(
            state,
            embed,
            ...args
          )
        )
    }
    this.dispatch.embed = embedActions as typeof this.dispatch.embed
  }

  getEmbed() {
    const state = this.getState()
    return state.embeds[state.activeEmbed]
  }

  setState(value: StoreState | ((state: StoreState) => StoreState)) {
    return new Promise<undefined>((resolve) => {
      let unsubscribe: () => void
      const handler = () => {
        unsubscribe()
        resolve(undefined)
      }
      unsubscribe = this.store.subscribe(handler)
      this.dispatch.setState(value)
    })
  }

  static useEmbed(): Embed
  static useEmbed<Selected extends unknown>(
    func: (state: Embed | undefined) => Selected,
    depends?: React.DependencyList
  ): Selected
  static useEmbed<Func extends (state: Embed | undefined) => any>(
    func: Func = identity as Func,
    depends?: React.DependencyList
  ) {
    return this.useSelector((state) => func(state.embeds[state.activeEmbed]), depends)
  }

  static usePlanner(): Profile
  static usePlanner<Selected extends unknown>(
    func: (state: Profile) => Selected,
    depends?: React.DependencyList
  ): Selected
  static usePlanner<Func extends (state: Profile) => any>(
    func: Func = identity as Func,
    depends?: React.DependencyList
  ) {
    return this.useSelector((state) => func(state.planner ?? defaultPlanner()), depends)
  }

  static useDispatch = RootStoreBase.useDispatch as () => StoreDispatch

  static useItem(id: ItemOrId) {
    return this.useSelector((state) => stateGetItem(state, id))
  }

  static instanceContext = React.createContext<Store>(undefined!)

  static Provider = ({
    store,
    children
  }: {
    store: typeof RootStoreBase extends new () => infer T ? T : never
    children: ReactNode
  }) => {
    const Provider = super.Provider
    return (
      <Store.instanceContext.Provider value={store as Store}>
        <Provider store={store}>{children}</Provider>
      </Store.instanceContext.Provider>
    )
  }

  static useInstance(): Store {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return React.useContext(Store.instanceContext)
  }
}
